import {
  Box,
  Container,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Badge,
  useDisclosure,
  useMediaQuery,
  Flex,
  IconButton,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  Checkbox,
  FormErrorMessage,
  useToast,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
} from '@chakra-ui/react'
import { Layout } from '@common'
import { ISSRPageData } from '@interfaces'
import { PageService } from '@services'
import useTranslation from 'next-translate/useTranslation'
import { useRouter } from 'next/router'
import { Calendar, momentLocalizer } from 'react-big-calendar'
import moment from 'moment'
import 'react-big-calendar/lib/css/react-big-calendar.css'
import 'moment/locale/tr'
import { useState, useEffect } from 'react'
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import axios from 'axios'
import React from 'react'

const localizer = momentLocalizer(moment)

// ✅ VALIDATION
const validationSchema = yup.object().shape({
  fullName: yup.string().required('Ad Soyad alanı zorunludur'),
  email: yup
    .string()
    .email('Geçerli bir e-posta adresi giriniz')
    .required('E-posta alanı zorunludur'),
  visitScope: yup.string().required('Ziyaret kapsamı seçiniz'),
  studentCount: yup
    .number()
    .typeError('Öğrenci sayısı bir sayı olmalıdır')
    .min(1, 'En az 1 öğrenci olmalıdır')
    .max(25, 'En fazla 25 öğrenci olabilir')
    .required('Öğrenci sayısı zorunludur'),
  institution: yup.string().required('Okul/Kurum adı zorunludur'),
  address: yup.string().required('Kurum adresi zorunludur'),
  phone: yup
    .string()
    .matches(/^[0-9]{10}$/, 'Telefon numarası 10 haneli olmalıdır (5xxxxxxxxx)')
    .required('Telefon numarası zorunludur'),
  isAccompanying: yup.boolean(),
  accompanyingName: yup.string().when('isAccompanying', {
    is: true,
    then: yup.string().required('Eşlik eden kişinin adı zorunludur'),
  }),
  accompanyingEmail: yup.string().when('isAccompanying', {
    is: true,
    then: yup
      .string()
      .email('Geçerli bir e-posta adresi giriniz')
      .required('Eşlik eden kişinin e-postası zorunludur'),
  }),
})

const ReservationFormScreen = ({ pageData, global }: ISSRPageData) => {
  const disabledSessions = pageData?.disabledSessions ?? {}
  const navigation = Array.isArray(global?.navigation) ? global.navigation : []

  const { t } = useTranslation()
  const { asPath } = useRouter()

  const { isOpen: isSessionOpen, onOpen: onSessionOpen, onClose: onSessionClose } = useDisclosure()
  const { isOpen: isFormOpen, onOpen: onFormOpen, onClose: onFormClose } = useDisclosure()

  const [isMobile] = typeof window !== 'undefined' ? useMediaQuery('(max-width: 768px)') : [false]
  const [currentDate, setCurrentDate] = useState(new Date())
  const [selectedSlot, setSelectedSlot] = useState<any>(null)
  const [selectedSession, setSelectedSession] = useState<any>(null)
  const [accompanyingPerson, setAccompanyingPerson] = useState(false)
  const toast = useToast()

  // ✅ Kapalı gün state
  const [closedDaysSet, setClosedDaysSet] = useState<Set<string>>(new Set())
  const [closedNotesMap, setClosedNotesMap] = useState<Record<string, string>>({})

  useEffect(() => {
    const fetchClosedDays = async () => {
      try {
        const res = await axios.get('https://sakipsabancimuzesi.org/bilet/closed-days')
        const data = Array.isArray(res.data) ? res.data : []
        const nextSet = new Set<string>()
        const nextMap: Record<string, string> = {}
        data.forEach((item: any) => {
          if (item?.date) {
            const key = moment(item.date).format('YYYY-MM-DD')
            nextSet.add(key)
            if (item?.note) nextMap[key] = String(item.note)
          }
        })
        setClosedDaysSet(nextSet)
        setClosedNotesMap(nextMap)
      } catch (e) {
        console.error('Kapalı günler alınamadı', e)
      }
    }
    fetchClosedDays()
  }, [])
  // react-hook-form kullanımı
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      fullName: '',
      email: '',
      visitScope: '',
      studentCount: 1,
      institution: '',
      address: '',
      phone: '',
      isAccompanying: false,
      accompanyingName: '',
      accompanyingEmail: '',
    },
  })

  // Eşlik eden kişi değişikliğini izle
  const watchIsAccompanying = watch('isAccompanying')
  useEffect(() => {
    setAccompanyingPerson(watchIsAccompanying)
  }, [watchIsAccompanying])

  // Gün geçerli mi kontrolü
  const isValidDay = (date: Date) => {
    if (!(date instanceof Date)) return false
    const day = date.getDay()
    const isOpenWeekday = day === 3 || day === 4 || day === 5
    const key = moment(date).format('YYYY-MM-DD')
    const isClosed = closedDaysSet.has(key)
    return isOpenWeekday && !isClosed
  }

  // Geçerli bir gün için müsait seansları al
  const getAvailableSessions = (date: Date) => {
    if (!isValidDay(date)) return []

    const day = date.getDay()
    const dayName = [
      'Pazar',
      'Pazartesi',
      'Salı',
      'Çarşamba',
      'Perşembe',
      'Cuma',
      'Cumartesi',
    ][day]
    const sessionType =
      day === 5
        ? 'Öğretmen Gözetiminde Serbest Ziyaret'
        : 'Rehber Eşliğinde Ücretsiz Tur'

    const sessions = [
      {
        title: `${sessionType} (10:00-11:00)`,
        start: moment(date).set({ hour: 10, minute: 0 }).toDate(),
        end: moment(date).set({ hour: 11, minute: 0 }).toDate(),
        type: day === 5 ? 'free' : 'guided',
        dayName: dayName,
        timeSlot: '10:00-11:00', // zaman dilimini ekliyoruz
      },
      {
        title: `${sessionType} (11:00-12:00)`,
        start: moment(date).set({ hour: 11, minute: 0 }).toDate(),
        end: moment(date).set({ hour: 12, minute: 0 }).toDate(),
        type: day === 5 ? 'free' : 'guided',
        dayName: dayName,
        timeSlot: '11:00-12:00', // zaman dilimini ekliyoruz
      },
      {
        title: `${sessionType} (13:00-14:00)`,
        start: moment(date).set({ hour: 13, minute: 0 }).toDate(),
        end: moment(date).set({ hour: 14, minute: 0 }).toDate(),
        type: day === 5 ? 'free' : 'guided',
        dayName: dayName,
        timeSlot: '13:00-14:00', // zaman dilimini ekliyoruz
      },
      {
        title: `${sessionType} (14:00-15:00)`,
        start: moment(date).set({ hour: 14, minute: 0 }).toDate(),
        end: moment(date).set({ hour: 15, minute: 0 }).toDate(),
        type: day === 5 ? 'free' : 'guided',
        dayName: dayName,
        timeSlot: '14:00-15:00', // zaman dilimini ekliyoruz
      },
    ]

    return sessions.map((session) => ({
      ...session,
      isDisabled: isSessionDisabled(date, session.timeSlot, disabledSessions),
    }))
  }

  const getAvailableSessionsWeb = (date: Date) => {
    if (!isValidDay(date)) return []

    const day = date.getDay()
    const dayName = [
      'Pazar',
      'Pazartesi',
      'Salı',
      'Çarşamba',
      'Perşembe',
      'Cuma',
      'Cumartesi',
    ][day]
    const sessionType =
      day === 5
        ? 'Öğretmen Gözetiminde Serbest Ziyaret'
        : 'Rehber Eşliğinde Ücretsiz Tur'

    return [
      {
        title: `${sessionType}`,
        start: moment(date).set({ hour: 10, minute: 0 }).toDate(),
        end: moment(date).set({ hour: 11, minute: 0 }).toDate(),
        type: day === 5 ? 'free' : 'guided',
        dayName: dayName,
      },
    ]
  }
  // Tüm ay için etkinlikleri oluştur
  const generateMonthEvents = (year: number, month: number) => {
    const startDate = new Date(year, month, -7) // Önceki haftadan başlasın
    const endDate = new Date(year, month + 1, 7) // Sonraki haftaya kadar uzasın

    let currentDate = new Date(startDate)
    const events = []

    while (currentDate <= endDate) {
      if (isValidDay(currentDate)) {
        const sessions = getAvailableSessionsWeb(new Date(currentDate))
        events.push(...sessions)
      }
      currentDate.setDate(currentDate.getDate() + 1)
    }

    return events
  }

  // Başlangıç etkinliklerini hesapla
  const today = new Date()
  const initialEvents = generateMonthEvents(
    today.getFullYear(),
    today.getMonth()
  )
  const [calendarEvents, setCalendarEvents] = useState(initialEvents)

  // Ay değiştiğinde etkinlikleri güncelle
  const handleNavigate = (date: Date) => {
    const newEvents = generateMonthEvents(date.getFullYear(), date.getMonth())
    setCalendarEvents(newEvents)
    setCurrentDate(date)
  }

  // Form gönderimi
  const onSubmit = (data) => {
    console.log('Form değerleri:', data)
    console.log('Seçilen seans:', selectedSession)

    const payload = {
      full_name: data.fullName,
      email: data.email,
      visit_scope: data.visitScope,
      student_count: data.studentCount,
      institution: data.institution,
      address: data.address,
      phone: data.phone,
      is_accompanying: data.isAccompanying || false,
      visit_date: selectedSession
        ? moment(selectedSession.start).format('YYYY-MM-DD')
        : null,
      visit_session: selectedSession
        ? `${moment(selectedSession.start).format('HH:mm')}-${moment(selectedSession.end).format('HH:mm')}`
        : null,

      accompanying_name: data.isAccompanying ? data.accompanyingName : null,
      accompanying_email: data.isAccompanying ? data.accompanyingEmail : null,
    }

    // Promise ile işlem
    axios
      .post('https://sakipsabancimuzesi.org/bilet/visit-request', payload, {
        headers: {
          'Content-Type': 'application/json',
        },
      })
      .then(() => {
        // Başarılı sonuç

        // Modalı kapat ve başarı mesajı göster
        onFormClose()
        toast({
          title: 'Rezervasyon alındı',
          description: `${moment(selectedSession.start).format(
            'DD MMMM YYYY - HH:mm'
          )} tarihindeki rezervasyonunuz başarıyla alındı.`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        })

        // Formu sıfırla
        reset()
        setAccompanyingPerson(false)
      })
      .catch((error) => {
        console.error('Rezervasyon gönderilemedi:', error)

        // Hata detaylarını göster
        const errorMessage =
          error.response?.data?.message ||
          'Rezervasyon gönderilirken bir sorun oluştu. Lütfen tekrar deneyin.'

        toast({
          title: 'Hata oluştu',
          description: errorMessage,
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
      })
  }

  const handleDayClick = (slotInfo: any) => {
    const selectedDateMoment = moment(slotInfo.start)
    const todayMoment = moment().startOf('day') // Bugünün başlangıcı

    // Geçmiş tarihleri kontrol et
    if (selectedDateMoment.isBefore(todayMoment)) {
      toast({
        title: 'Geçersiz Tarih',
        description: 'Geçmiş tarihler için rezervasyon yapılamaz.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    // Mevcut isValidDay kontrolünüz
    if (!isValidDay(slotInfo.start)) {
      toast({
        title: 'Uygun olmayan gün',
        description: 'Bu günde ziyaret yapılamamaktadır.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    setSelectedSlot(slotInfo)
    onSessionOpen()
  }

  // Seans seçimi
  const handleSessionSelect = (session: any) => {
    setSelectedSession(session)
    onSessionClose()
    onFormOpen()
  }

  // Gün stilini alma
  const dayPropGetter = (date: Date) => {
    // Kapalı gün veya uygun olmayan günleri stillendir
    if (!isValidDay(date)) {
      const key = moment(date).format('YYYY-MM-DD')
      const hasNote = !!closedNotesMap[key]
      return {
        style: {
          backgroundColor: '#F2F2F7',
          opacity: 0.6,
          cursor: 'not-allowed',
          position: 'relative',
        },
        className: hasNote ? 'museum-closed has-note' : 'museum-closed',
      } as any
    }
    return {} as any
  }

  // Gün hücresi sarmalayıcısı: notu title olarak göster
  const DateCellWrapper: React.FC<any> = ({ children, value }) => {
    const key = moment(value).format('YYYY-MM-DD')
    const note = closedNotesMap[key]
    if (note) {
      return <div title={note}>{children}</div>
    }
    return <div>{children}</div>
  }

  // Etkinlik stilini alma
  const eventStyleGetter = (event: any) => {
    const now = moment()
    const eventEndMoment = moment(event.end)

    // Eğer etkinliğin bitiş zamanı şu anki zamandan önceyse, gizle
    if (eventEndMoment.isBefore(now)) {
      return {
        style: {
          display: 'none', // Etkinliği tamamen gizle
        },
      }
    }

    // Geçmişte değilse, mevcut stil mantığını uygula
    const backgroundColor = event.type === 'guided' ? '#ba3b46' : '#548687'

    return {
      style: {
        backgroundColor,
        color: '#FFFFFF',
        border: 'none',
        fontSize: '13px',
        padding: '4px 8px',
        opacity: 0.9,
        transition: 'all 0.2s ease',
        cursor: 'pointer',
      },
    }
  }

  // Mobil gün gezinimi
  const goToPreviousDay = () => {
    setCurrentDate(moment(currentDate).subtract(1, 'day').toDate())
  }

  const goToNextDay = () => {
    setCurrentDate(moment(currentDate).add(1, 'day').toDate())
  }

  const goToToday = () => {
    setCurrentDate(new Date())
  }

  // Seçilen gün için etkinlikleri al
  const getDayEvents = (date: Date) => {
    if (!isValidDay(date)) return []
    return getAvailableSessions(date)
  }

  // Mobil gün görünümü
  const MobileDayView = () => {
    const dayEvents = getDayEvents(currentDate)
    const isAvailable = isValidDay(currentDate)
    const dateFormat = 'DD MMMM YYYY, dddd'
    const today = moment()
    const selectedDate = moment(currentDate)

    return (
      <Box>
        <Flex
          justifyContent="space-between"
          alignItems="center"
          mb={4}
          bg="gray.50"
          p={3}
          borderRadius="md"
        >
          <IconButton
            icon={<ChevronLeftIcon />}
            onClick={goToPreviousDay}
            aria-label="Önceki gün"
            size="sm"
          />

          <Text fontWeight="bold" textAlign="center">
            {moment(currentDate).format(dateFormat)}
          </Text>

          <IconButton
            icon={<ChevronRightIcon />}
            onClick={goToNextDay}
            aria-label="Sonraki gün"
            size="sm"
          />
        </Flex>

        {selectedDate.isSame(today, 'day') === false && (
          <Flex justifyContent="space-between" mb={4}>
            <Button size="sm" variant="primary" onClick={goToToday} w="full">
              Bugün
            </Button>
          </Flex>
        )}

        {/* Ziyarete Kapalı Mesajı */}
        {!isAvailable && (
          <Box bg="gray.100" p={4} borderRadius="md" mb={4} textAlign="center">
            <Text fontWeight="medium" color="gray.600">
              Bu gün ziyarete kapalıdır.
            </Text>
          </Box>
        )}

        {isAvailable && dayEvents.length > 0 ? (
          <VStack spacing={4} align="stretch">
            {dayEvents.map((event, index) => (
              <Box
                key={index}
                p={4}
                bg="white"
                border="1px solid #F0EFEE"
                onClick={() => handleSessionSelect(event)}
                cursor="pointer"
                _hover={{ bg: 'gray.50' }}
              >
                <Flex justifyContent="space-between" alignItems="center" mb={2}>
                  <Badge
                    bg={event.type === 'guided' ? '#ba3b46' : '#548687'}
                    color="white"
                    borderRadius="0"
                    fontFamily="mikado"
                    letterSpacing="1px"
                    fontSize="12px"
                    pt={0.5}
                  >
                    {event.type === 'guided'
                      ? 'Rehberli Tur'
                      : 'Serbest Ziyaret'}
                  </Badge>

                  <Text fontSize="sm" color="gray.600" fontWeight="medium">
                    {moment(event.start).format('HH:mm')} -{' '}
                    {moment(event.end).format('HH:mm')}
                  </Text>
                </Flex>

                <Text fontWeight="bold" mb={1}>
                  {event.dayName}
                </Text>
              </Box>
            ))}
          </VStack>
        ) : (
          isAvailable && (
            <Box bg="gray.50" p={6} borderRadius="md" textAlign="center">
              <Text color="gray.500">Seansa erişilemedi.</Text>
            </Box>
          )
        )}
      </Box>
    )
  }

  return (
    <Layout
      title={t('common:ssmPageTitle')}
      navigation={navigation}
      path={asPath}
    >
      <Container mt="1" maxW="container.xl">
        <Text
          as="h2"
          color="red.primary"
          fontSize={{ base: '2xl', lg: '3xl' }}
          my={10}
          mb={{ base: 'padding.primary', lg: 'padding.primary' }}
        >
          Müze Ziyaret Rezervasyonu
        </Text>

        <Box
          mb={6}
          p={4}
          borderRadius="md"
          bg="white.primary2"
          borderLeft="4px solid"
          borderColor="white.primary3"
          boxShadow="sm"
        >
          <Text
            fontWeight="semibold"
            fontSize="md"
            mb={3}
            color="black.primary5"
          >
            Ziyaret Günleri:
          </Text>
          <VStack spacing={2} align="start">
            <Text color="red.primary">Çarşamba: Rehber Eşliğinde</Text>
            <Text color="red.primary">Perşembe: Rehber Eşliğinde</Text>
            <Text color="#659293">Cuma: Öğretmen Gözetiminde</Text>
          </VStack>
        </Box>

        <Box
          mb={8}
          sx={{
            '.rbc-calendar': {
              backgroundColor: '#FFFFFF',
              fontFamily:
                '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            },
            '.rbc-toolbar': {
              marginBottom: '20px',
              padding: '0 10px',
            },
            '.rbc-toolbar button': {
              color: '#000000',
              border: '1px solid #E5E5EA',
              backgroundColor: '#FFFFFF',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.2s ease',
              _hover: {
                backgroundColor: '#F2F2F7',
              },
              _active: {
                backgroundColor: '#007AFF',
                color: '#FFFFFF',
                borderColor: '#007AFF',
              },
            },
            '.rbc-active': {
              backgroundColor: '#c71f3f !important',
              color: '#FFFFFF !important',
              borderColor: '#c71f3f !important',
            },
            '.rbc-header': {
              padding: '10px',
              backgroundColor: '#faf7f0',
              borderColor: '#E5E5EA',
              color: '#1C1C1E',
              fontWeight: '600',
              fontSize: '13px',
            },
            '.rbc-today': {
              backgroundColor: '#F2F2F7',
            },
            '.rbc-off-range-bg': {
              backgroundColor: '#FAFAFA',
            },
            '.rbc-month-view, .rbc-time-view': {
              borderColor: '#E5E5EA',
              backgroundColor: '#FFFFFF',
            },
            '.rbc-date-cell': {
              padding: '8px',
              fontSize: '13px',
              color: '#1C1C1E',
            },
            '.museum-closed': {
              position: 'relative',
            },
            '.rbc-event': {
              transition: 'all 0.2s ease',
              cursor: 'pointer',
              whiteSpace: 'normal !important',
              height: 'auto !important',
              _hover: {
                opacity: '1',
                transform: 'scale(1.01)',
              },
            },
            '.rbc-row': {
              height: 'auto !important',
            },
            '.rbc-row-segment': {
              height: 'auto !important',
              padding: '2px 1px !important',
            },
            '.rbc-row-content': {
              height: 'auto !important',
            },
            '.rbc-event-content': {
              whiteSpace: 'normal !important',
              height: 'auto !important',
              textOverflow: 'clip !important',
              overflow: 'visible !important',
            },
            '.rbc-show-more': {
              display: 'none !important',
            },
            '.rbc-month-row': {
              height: 'auto !important',
              overflow: 'visible !important',
            },
          }}
        >
          {isMobile ? (
            <MobileDayView />
          ) : (
            <Calendar
              localizer={localizer}
              events={calendarEvents}
              startAccessor="start"
              endAccessor="end"
              style={{ height: 700 }}
              views={['month']}
              defaultView="month"
              messages={{
                today: 'Bugün',
                previous: 'Önceki',
                next: 'Sonraki',
                month: 'Ay',
              }}
              eventPropGetter={eventStyleGetter}
              dayPropGetter={dayPropGetter}
              onNavigate={handleNavigate}
              selectable
              showAllEvents={true}
              onSelectEvent={handleDayClick}
              onSelectSlot={handleDayClick}
              components={{ dateCellWrapper: DateCellWrapper }}
            />
          )}
        </Box>

        {/* Seans Seçim Modalı */}
        <Modal
          isOpen={isSessionOpen}
          onClose={onSessionClose}
          size="md"
          isCentered
        >
          <ModalOverlay bg="rgba(0,0,0,0.6)" />
          <ModalContent borderRadius="sm">
            <ModalHeader>Ziyaret Saati Seçin</ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              {selectedSlot && (
                <VStack spacing={4} align="stretch">
                  <Text fontWeight="bold">
                    {moment(selectedSlot.start).format('DD MMMM YYYY, dddd')}
                  </Text>

                  {getAvailableSessions(selectedSlot.start).map(
                    (session, index) => (
                      <Button
                        key={index}
                        onClick={() => handleSessionSelect(session)}
                        variant="outline"
                        justifyContent="space-between"
                        height="auto"
                        py={3}
                        isDisabled={session.isDisabled}
                      >
                        <Text
                          color={
                            session.isDisabled
                              ? 'white.primary'
                              : 'black.primary'
                          }
                        >
                          {moment(session.start).format('HH:mm')} -{' '}
                          {moment(session.end).format('HH:mm')}
                        </Text>
                        <Badge
                          colorScheme={
                            session.isDisabled
                              ? 'gray'
                              : session.type === 'guided'
                              ? 'red'
                              : 'teal'
                          }
                        >
                          {session.isDisabled
                            ? 'Dolu'
                            : session.type === 'guided'
                            ? 'Rehberli Tur'
                            : 'Serbest Ziyaret'}
                        </Badge>
                      </Button>
                    )
                  )}
                </VStack>
              )}
            </ModalBody>
          </ModalContent>
        </Modal>

        {/* Rezervasyon Form Modalı */}
        <Modal isOpen={isFormOpen} onClose={onFormClose} size="lg">
          <ModalOverlay bg="rgba(0,0,0,0.6)" />
          <ModalContent borderRadius="sm">
            <ModalHeader borderBottomWidth="1px">Rezervasyon Formu</ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              {selectedSession && (
                <Box mb={4}>
                  <HStack mb={2}>
                    <Badge
                      colorScheme={
                        selectedSession.type === 'guided' ? 'red' : 'teal'
                      }
                      fontSize="sm"
                    >
                      {selectedSession.type === 'guided'
                        ? 'Rehberli Tur'
                        : 'Serbest Ziyaret'}
                    </Badge>
                    <Text fontWeight="medium">
                      {moment(selectedSession.start).format('DD MMMM YYYY')}
                    </Text>
                  </HStack>
                  <Text fontSize="lg" fontWeight="bold">
                    {moment(selectedSession.start).format('HH:mm')} -{' '}
                    {moment(selectedSession.end).format('HH:mm')}
                  </Text>
                </Box>
              )}

              <form onSubmit={handleSubmit(onSubmit)}>
                <VStack spacing={4} align="stretch">
                  <FormControl isInvalid={errors.fullName !== undefined}>
                    <FormLabel>Ad Soyad</FormLabel>
                    <Input name="fullName" ref={register} />
                    <FormErrorMessage>
                      {errors.fullName?.message}
                    </FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={errors.email !== undefined}>
                    <FormLabel>E-posta</FormLabel>
                    <Input name="email" type="email" ref={register} />
                    <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={errors.visitScope !== undefined}>
                    <FormLabel>Ziyaret Kapsamı</FormLabel>
                    <Select
                      name="visitScope"
                      placeholder="Seçiniz"
                      ref={register}
                    >
                      <option value="Atlı Köşk">Atlı Köşk</option>
                      <option value="Süreli Sergiler">Süreli Sergiler</option>
                      <option value="Bahçe Turu">Bahçe Turu</option>
                    </Select>
                    <FormErrorMessage>
                      {errors.visitScope?.message}
                    </FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={errors.studentCount !== undefined}>
                    <FormLabel>Öğrenci Sayısı (Maksimum 25)</FormLabel>
                    <NumberInput
                      max={25}
                      min={1}
                      defaultValue={1}
                      onChange={(valueString) =>
                        setValue('studentCount', parseInt(valueString))
                      }
                    >
                      <NumberInputField name="studentCount" ref={register} />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                    <FormErrorMessage>
                      {errors.studentCount?.message}
                    </FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={errors.institution !== undefined}>
                    <FormLabel>Okul / Kurum Adı</FormLabel>
                    <Input name="institution" ref={register} />
                    <FormErrorMessage>
                      {errors.institution?.message}
                    </FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={errors.address !== undefined}>
                    <FormLabel>Kurum Adresi</FormLabel>
                    <Textarea name="address" rows={3} ref={register} />
                    <FormErrorMessage>
                      {errors.address?.message}
                    </FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={errors.phone !== undefined}>
                    <FormLabel>Cep Telefonu</FormLabel>
                    <Input
                      name="phone"
                      ref={register}
                      placeholder="5xxxxxxxxx"
                    />
                    <FormErrorMessage>{errors.phone?.message}</FormErrorMessage>
                  </FormControl>

                  <FormControl>
                    <Checkbox
                      name="isAccompanying"
                      ref={register}
                      onChange={(e) => setAccompanyingPerson(e.target.checked)}
                    >
                      Bu kişi, aynı zamanda ziyaret günü gruba eşlik edecektir.
                    </Checkbox>
                  </FormControl>

                  {accompanyingPerson && (
                    <Box mt={2} p={4} bg="gray.50" borderRadius="md">
                      <Text fontWeight="medium" mb={2}>
                        Gruba Eşlik Edecek Kişi Bilgileri
                      </Text>

                      <FormControl
                        mb={3}
                        isInvalid={errors.accompanyingName !== undefined}
                      >
                        <FormLabel fontSize="sm">Ad Soyad</FormLabel>
                        <Input
                          name="accompanyingName"
                          ref={register}
                          bg="white"
                          size="sm"
                          placeholder="Eşlik eden kişinin adı soyadı"
                        />
                        <FormErrorMessage>
                          {errors.accompanyingName?.message}
                        </FormErrorMessage>
                      </FormControl>

                      <FormControl
                        isInvalid={errors.accompanyingEmail !== undefined}
                      >
                        <FormLabel fontSize="sm">E-posta</FormLabel>
                        <Input
                          name="accompanyingEmail"
                          type="email"
                          ref={register}
                          bg="white"
                          size="sm"
                          placeholder="Eşlik eden kişinin e-postası"
                        />
                        <FormErrorMessage>
                          {errors.accompanyingEmail?.message}
                        </FormErrorMessage>
                      </FormControl>
                    </Box>
                  )}

                  <Button mt={4} colorScheme="red" type="submit">
                    Rezervasyon Yap
                  </Button>
                </VStack>
              </form>
            </ModalBody>
          </ModalContent>
        </Modal>
      </Container>
    </Layout>
  )
}

export default ReservationFormScreen

export async function getStaticProps({ locale }: { locale: string }) {
  const pageData = {
    data: null,
    error: null,
    disabledSessions: {},
  }

  let global = null

  try {
    const collection = await PageService.getHomePage(locale)
    const disabledSessions = await PageService.getVisitReservations()
    pageData.data = collection
    pageData.disabledSessions = disabledSessions
  } catch (err) {
    if (err instanceof Error) {
      pageData.error = { message: err.message }
    }
  }

  return {
    props: { pageData, global },
    revalidate: 120,
  }
}
